<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useTicketsStore } from 'dashboard/stores/tickets';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TicketAttachmentsInput from './TicketAttachmentsInput.vue';
import TicketStatusBadge from './TicketStatusBadge.vue';
import { formatTicketDate, formatTicketDateTime } from './formatters';

const props = defineProps({
  ticketId: { type: Number, required: true },
});

const emit = defineEmits(['back']);

const { t, locale } = useI18n();
const store = useTicketsStore();

const ticket = ref(null);
const reply = ref('');
const replyAttachments = ref([]);
const attachmentsInput = ref(null);

const formatDateTime = timestamp =>
  formatTicketDateTime(timestamp, locale.value);
const formatDate = isoDate => formatTicketDate(isoDate, locale.value);

const formatContent = content => new MessageFormatter(content).formattedMessage;

const details = computed(() =>
  [
    [t('TICKETS.DETAIL.TYPE'), ticket.value.ticketType],
    [
      t('TICKETS.DETAIL.STARTED_ON'),
      ticket.value.startedOn && formatDate(ticket.value.startedOn),
    ],
    [t('TICKETS.DETAIL.CONTACT_PHONE'), ticket.value.contactPhone],
    [t('TICKETS.DETAIL.NOTIFICATION_EMAIL'), ticket.value.notificationEmail],
    [
      t('TICKETS.DETAIL.PARTICIPANTS'),
      ticket.value.participants.map(participant => participant.name).join(', '),
    ],
  ].filter(([, value]) => value)
);

const canReply = computed(() => reply.value.trim().length > 0);

// 403 traz do servidor a mensagem de sem acesso no idioma do usuario.
const loadTicket = async () => {
  try {
    ticket.value = await store.fetchTicket(props.ticketId);
  } catch (error) {
    const { status, data } = error?.response || {};
    if (status === 403) useAlert(data.message);
    else if (status === 404) useAlert(t('TICKETS.DETAIL.NOT_FOUND'));
    else useAlert(t('TICKETS.DETAIL.LOAD_ERROR'));
    emit('back');
  }
};

const sendReply = async () => {
  if (!canReply.value) return;
  try {
    ticket.value = await store.createComment(props.ticketId, {
      content: reply.value.trim(),
      attachments: replyAttachments.value,
    });
    reply.value = '';
    replyAttachments.value = [];
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TICKETS.DETAIL.COMMENT_ERROR')
    );
  }
};

const onPaste = event => attachmentsInput.value?.onPaste(event);

onMounted(loadTicket);
</script>

<template>
  <div class="flex flex-col gap-4">
    <Button
      variant="link"
      color="slate"
      size="sm"
      type="button"
      icon="i-lucide-arrow-left"
      class="self-start"
      :label="t('TICKETS.DETAIL.BACK')"
      @click="emit('back')"
    />
    <div v-if="ticket" class="flex flex-col gap-5">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ ticket.displayId }}
          </span>
          <TicketStatusBadge :status="ticket.status" />
        </div>
        <h4 class="m-0 text-lg font-medium text-n-slate-12">
          {{ ticket.title }}
        </h4>
        <span class="text-xs text-n-slate-11">
          {{
            t('TICKETS.DETAIL.OPENED_BY', {
              name: ticket.requesterName,
              date: formatDateTime(ticket.createdAt),
            })
          }}
        </span>
      </div>

      <dl class="grid grid-cols-1 gap-x-4 gap-y-2 m-0 text-sm sm:grid-cols-2">
        <div v-for="[label, value] in details" :key="label">
          <dt class="text-xs text-n-slate-11">{{ label }}</dt>
          <dd class="m-0 text-n-slate-12 break-words">{{ value }}</dd>
        </div>
      </dl>

      <div
        v-dompurify-html="formatContent(ticket.description)"
        class="p-3 text-sm rounded-lg prose prose-bubble max-w-none bg-n-alpha-1 text-n-slate-12"
      />

      <ul
        v-if="ticket.attachments.length"
        class="flex flex-wrap gap-2 m-0 list-none"
      >
        <li v-for="attachment in ticket.attachments" :key="attachment.id">
          <a
            :href="attachment.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-n-alpha-1 text-n-slate-12 hover:bg-n-alpha-2"
          >
            <Icon icon="i-lucide-paperclip" class="size-3" />
            {{ attachment.filename }}
          </a>
        </li>
      </ul>

      <div class="flex flex-col gap-3">
        <h5 class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.DETAIL.ACTIVITY') }}
        </h5>
        <p v-if="!ticket.activities.length" class="m-0 text-sm text-n-slate-11">
          {{ t('TICKETS.DETAIL.NO_ACTIVITY') }}
        </p>
        <template
          v-for="activity in ticket.activities"
          :key="`${activity.kind}-${activity.id}`"
        >
          <div
            v-if="activity.kind === 'status_change'"
            class="flex items-center gap-2 text-xs text-n-slate-11"
          >
            <Icon icon="i-lucide-refresh-cw" class="size-3" />
            <span>
              {{
                t('TICKETS.DETAIL.STATUS_CHANGED', {
                  status: activity.statusName,
                })
              }}
            </span>
            <span>· {{ formatDateTime(activity.createdAt) }}</span>
          </div>
          <div
            v-else
            class="flex flex-col gap-1 p-3 rounded-lg outline outline-1 -outline-offset-1"
            :class="
              activity.fromSupport
                ? 'outline-n-blue-6 bg-n-blue-2'
                : 'outline-n-weak'
            "
          >
            <div class="flex items-center gap-2 text-xs">
              <span class="font-medium text-n-slate-12">
                {{ activity.authorName }}
              </span>
              <span
                v-if="activity.fromSupport"
                class="px-1.5 rounded bg-n-blue-3 text-n-blue-11"
              >
                {{ t('TICKETS.DETAIL.SUPPORT') }}
              </span>
              <span class="text-n-slate-11">
                {{ formatDateTime(activity.createdAt) }}
              </span>
            </div>
            <div
              v-dompurify-html="formatContent(activity.content)"
              class="text-sm prose prose-bubble max-w-none text-n-slate-12"
            />
            <div
              v-if="activity.attachments.length"
              class="flex flex-wrap gap-2"
            >
              <a
                v-for="attachment in activity.attachments"
                :key="attachment.id"
                :href="attachment.url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-n-alpha-1 text-n-slate-12 hover:bg-n-alpha-2"
              >
                <Icon icon="i-lucide-paperclip" class="size-3" />
                {{ attachment.filename }}
              </a>
            </div>
          </div>
        </template>
      </div>

      <div class="flex flex-col gap-2" @paste="onPaste">
        <TextArea
          v-model="reply"
          :placeholder="t('TICKETS.DETAIL.COMMENT_PLACEHOLDER')"
          auto-height
          min-height="4rem"
        />
        <TicketAttachmentsInput
          ref="attachmentsInput"
          v-model="replyAttachments"
        />
        <Button
          type="button"
          class="self-end"
          :label="t('TICKETS.DETAIL.SEND')"
          :disabled="!canReply"
          :is-loading="store.uiFlags.isCommenting"
          @click="sendReply"
        />
      </div>
    </div>
  </div>
</template>
